import features from '@common/constants/features';

import { apiManager } from '@/api/api-manager/api-manager';
import { useStore } from '@/store';
import { ttlKeys, useTtlStorage } from '@/stores/ttl-storage/ttl-storage';
import { sanitize } from '@/utils/sanitize';

// #region [helpers]

export async function getPopUpContent(): Promise<string> {
  const [item] = await apiManager.$api.getStaticFragment('promo_registration_popup').catch(() => []);

  if (!item?.isActive || !item.content) {
    return '';
  }

  return sanitize(item.content);
}

const sessionTimeout = 'popupTimeout';

/**
 * Функция возвращающая количество setTimeout(,ms) которое нужно до показа модалки.
 * Если зашли первый раз, вернется на 3 мин.
 * Если страница перезагружалась, то вернется время оставшееся до истечения 3 минут выставленных ранее.
 * Если таймер истек, вернется 0, что бы можно было подставить в setTimeout
 */
export function getLeftTimeoutValue(): number {
  if (typeof sessionStorage === 'undefined') {
    return 0; // Для сборки ssr
  }

  let time = Number(sessionStorage.getItem(sessionTimeout));

  if (!time) {
    // нет ключа в сторе, ставим время 3 минуты спустя
    time = Date.now() + 1000 * 60 * 3;
    sessionStorage.setItem(sessionTimeout, `${time}`);
  }

  // Округляем для тестов
  const val = Math.ceil((time - Date.now()) / 10000) * 10000;

  if (val > 0) {
    return val;
  }

  // Если таймер уже истек
  return 0;
}

function resetPopUpTimer() {
  if (typeof sessionStorage === 'undefined') {
    return; // Для сборки ssr
  }
  sessionStorage.removeItem(sessionTimeout);
}

// #endregion

/**
 * Composition для отображения модального окна
 *
 */
export function usePopUpActions() {
  const store = useStore();
  const ttlStorage = useTtlStorage();

  function isLoggedIn(): boolean {
    return !!store.getters['user/loggedIn'];
  }

  function isFeatureEnabled(): boolean {
    return store.getters['features/$isFeatureEnabled'](features.REGISTRATION_POP_UP);
  }

  function hasOpenedModal(): boolean {
    return !!document.querySelector('.modal');
  }

  function isAvailableForShow() {
    return ttlStorage.isAvailable(ttlKeys.hasSeenBonusPopUp);
  }

  function setTtlExpireTime() {
    const expireDate = new Date();
    expireDate.setHours(23);
    expireDate.setMinutes(59);
    expireDate.setSeconds(59);

    ttlStorage.setExpireTime('hasSeenBonusPopUp', expireDate);
  }

  /**
   * Функция для показа модального окна
   */
  async function processModal(callback: (content: string) => void): Promise<void> {
    if (!isFeatureEnabled()) {
      return;
    }

    if (isLoggedIn()) {
      setTtlExpireTime();
      return;
    }

    if (!isAvailableForShow()) {
      return;
    }

    // Пауза перед показом попапа
    const timeoutTime = getLeftTimeoutValue();
    await new Promise((ok) => { setTimeout(ok, timeoutTime); });

    // Если открыто другое модальное окно, ждем минуту
    if (hasOpenedModal()) {
      setTimeout(() => {
        processModal(callback);
      }, 1000 * 60);

      return;
    }

    const content = await getPopUpContent();

    callback(content);

    setTtlExpireTime();
    resetPopUpTimer();
  }

  /**
   *
   */
  return {
    processModal,
  };
}
